import { useState } from "react";
import PlayerTable from "@/components/players/PlayerTable";
import PlayerFilters from "@/components/players/PlayerFilters";
import DeletedPlayersTable from "@/components/players/DeletedPlayersTable";
import PlayerMessages from "@/components/messages/PlayerMessages";
import { UnitsToggle } from "@/components/ui/units-toggle";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { useQuery } from "@tanstack/react-query";
import { getNotificationCounts } from "@/lib/api/notifications";

export default function PlayerDatabase() {
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [includeMissingValues, setIncludeMissingValues] = useState<boolean>(true);
  const [filteredPlayerCount, setFilteredPlayerCount] = useState<number>(0);
  
  // Check URL params for initial tab
  const urlParams = new URLSearchParams(window.location.search);
  const initialTab = urlParams.get('tab') || 'active';
  const [activeTab, setActiveTab] = useState<"active" | "deleted" | "messages">(
    initialTab as "active" | "deleted" | "messages"
  );
  
  // Fetch notification counts for messages tab badge
  const { data: notificationCounts } = useQuery({
    queryKey: ["notification-counts"],
    queryFn: getNotificationCounts,
    refetchInterval: 30000, // Refresh every 30 seconds
  });
  
  // Handle filter changes
  const handleFilterChange = (newFilters: Record<string, string>) => {
    setFilters(newFilters);
  };
  
  // Handle filter removal (when columns are hidden)
  const handleFilterRemoval = (filtersToRemove: string[]) => {
    setFilters(prevFilters => {
      const updatedFilters = { ...prevFilters };
      filtersToRemove.forEach(filterKey => {
        delete updatedFilters[filterKey];
      });
      return updatedFilters;
    });
  };
  
  return (
    <div className="px-4 py-6 sm:px-6 lg:px-8">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between mb-6">
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl font-semibold text-neutral-800">Player Database</h1>
          <p className="text-sm text-neutral-500">Manage and filter your scouting database</p>
        </div>
        
        {/* Toggle Controls */}
        <div className="mt-4 sm:mt-0 flex flex-col sm:flex-row items-end gap-3">
          <div className="flex items-center px-3 py-1.5 bg-white rounded-lg border border-neutral-200 shadow-sm">
            <div className="flex-1 mr-3">
              <label htmlFor="missing-values-toggle" className="text-sm font-medium text-neutral-700">
                {includeMissingValues ? "Include missing values" : "Exclude missing values"}
              </label>
            </div>
            <Switch
              id="missing-values-toggle"
              checked={includeMissingValues}
              onCheckedChange={setIncludeMissingValues}
            />
          </div>
          <UnitsToggle />
        </div>
        
        {/* Mobile view actions */}
        <div className="mt-4 sm:hidden flex space-x-3">
          <button className="py-2 px-4 bg-neutral-100 text-neutral-700 rounded-md text-sm font-medium hover:bg-neutral-200 flex-1 flex justify-center items-center">
            <i className="fas fa-download mr-2"></i> Export
          </button>
        </div>
      </div>
      
      <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as "active" | "deleted" | "messages")} className="w-full">
        <TabsList className="grid w-full max-w-[600px] grid-cols-3">
          <TabsTrigger value="active">Active Players</TabsTrigger>
          <TabsTrigger value="deleted">Deleted Players</TabsTrigger>
          <TabsTrigger value="messages" className="relative">
            Messages
            {notificationCounts && notificationCounts.player_messages > 0 && (
              <Badge 
                variant="destructive" 
                className="ml-2 h-5 px-1.5 text-xs"
              >
                {notificationCounts.player_messages}
              </Badge>
            )}
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="active" className="mt-6">
          {/* Filters and Search */}
          <div className="mb-4 w-full">
            <PlayerFilters 
              onFilterChange={handleFilterChange} 
              filteredPlayerCount={filteredPlayerCount}
            />
          </div>

          {/* Player Table */}
          <PlayerTable 
            filters={filters} 
            includeMissingValues={includeMissingValues} 
            onFilteredCountChange={setFilteredPlayerCount}
            onFilterRemoval={handleFilterRemoval}
          />
        </TabsContent>
        
        <TabsContent value="deleted" className="mt-6">
          <DeletedPlayersTable />
        </TabsContent>
        
        <TabsContent value="messages" className="mt-6">
          <PlayerMessages />
        </TabsContent>
      </Tabs>
    </div>
  );
}
